import { useState } from "react";

const BASE_URL = process.env.NODE_API_URL;

export class ApiRequestError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
  }
}

const getErrorMessage = (error: unknown): string =>
  error instanceof Error && error.message
    ? error.message
    : "Ocorreu um erro ao processar a solicitação.";

const getAuthorization = (): string | undefined => {
  const storedSession =
    localStorage.getItem("s") ?? sessionStorage.getItem("s");

  if (!storedSession) return undefined;

  try {
    const parsedSession: unknown = JSON.parse(storedSession);
    return typeof parsedSession === "string" ? parsedSession : undefined;
  } catch {
    return storedSession;
  }
};

const useApi = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRequest = async (
    endpoint: string,
    method: string,
    data?: object,
    headers?: Record<string, string>,
    config?: { platform?: boolean }
  ): Promise<Response> => {
    try {
      setIsLoading(true);
      setError(null);

      const companyId = localStorage.getItem("companyId");
      const authorization = getAuthorization();

      const options: RequestInit = {
        method,
        headers: {
          "Content-Type": "application/json",
          ...(authorization ? { Authorization: authorization } : {}),
          // Telas de contexto GERAL passam { platform: true } p/ não enviar o tenant.
          ...(companyId && !config?.platform
            ? { "X-Company-Id": companyId }
            : {}),
          ...headers,
        },
      };

      if (data) {
        options.body = JSON.stringify(data);
      }

      const response = await fetch(`${BASE_URL}${endpoint}`, options);

      if (!response.ok) {
        const rawBody = await response.text();
        let parsedBody: unknown = rawBody;

        try {
          parsedBody = rawBody ? JSON.parse(rawBody) : null;
        } catch {
          parsedBody = rawBody;
        }

        const errorMessage =
          parsedBody &&
          typeof parsedBody === "object" &&
          "message" in parsedBody &&
          typeof parsedBody.message === "string"
            ? parsedBody.message
            : rawBody || response.statusText;
        throw new ApiRequestError(errorMessage, response.status);
      }

      return response;
    } catch (requestError: unknown) {
      setError(getErrorMessage(requestError));
      throw requestError;
    } finally {
      setIsLoading(false);
    }
  };

  const getRequest = async (
    endpoint: string,
    headers?: Record<string, string>,
    config?: { platform?: boolean }
  ): Promise<Response> => {
    return handleRequest(endpoint, "GET", undefined, headers, config);
  };

  const postRequest = async (
    endpoint: string,
    data: object,
    headers?: Record<string, string>,
    config?: { platform?: boolean }
  ): Promise<Response> => {
    return handleRequest(endpoint, "POST", data, headers, config);
  };

  const putRequest = async (
    endpoint: string,
    data: object,
    headers?: Record<string, string>,
    config?: { platform?: boolean }
  ): Promise<Response> => {
    return handleRequest(endpoint, "PUT", data, headers, config);
  };

  const patchRequest = async (
    endpoint: string,
    data: object,
    headers?: Record<string, string>,
    config?: { platform?: boolean }
  ): Promise<Response> => {
    return handleRequest(endpoint, "PATCH", data, headers, config);
  };

  const deleteRequest = async (
    endpoint: string,
    headers?: Record<string, string>,
    config?: { platform?: boolean }
  ): Promise<Response> => {
    return handleRequest(endpoint, "DELETE", undefined, headers, config);
  };

  return {
    getRequest,
    postRequest,
    putRequest,
    patchRequest,
    deleteRequest,
    isLoading,
    error,
  };
};

export default useApi;
